import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, XCircle, AlertCircle, X } from "lucide-react";

const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = "success") => {
        const id = Math.random().toString(36).substr(2, 9);
        setToasts((prev) => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 5000);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            
            <div style={{
                position: "fixed",
                bottom: "2rem",
                right: "2rem",
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                pointerEvents: "none"
            }}>
                <AnimatePresence>
                    {toasts.map((toast) => (
                        <motion.div
                            key={toast.id}
                            initial={{ opacity: 0, x: 50, scale: 0.9 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: 20, scale: 0.9 }}
                            style={{
                                pointerEvents: "auto",
                                background: "rgba(10, 10, 10, 0.9)",
                                backdropFilter: "blur(12px)",
                                border: `1px solid ${toast.type === 'success' ? '#00ff9933' : '#ff4d4d33'}`,
                                borderRadius: "12px",
                                padding: "1rem 1.25rem",
                                minWidth: "300px",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
                                color: "#fff"
                            }}
                        >
                            {toast.type === 'success' ? (
                                <CheckCircle size={20} style={{ color: "#00ff99" }} />
                            ) : (
                                <AlertCircle size={20} style={{ color: "#ff4d4d" }} />
                            )}
                            
                            <div style={{ flex: 1, fontSize: "0.95rem", fontWeight: "500" }}>
                                {toast.message}
                            </div>

                            <button
                                onClick={() => removeToast(toast.id)}
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "rgba(255,255,255,0.4)",
                                    cursor: "pointer",
                                    padding: "4px",
                                    display: "flex"
                                }}
                            >
                                <X size={16} />
                            </button>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) throw new Error("useToast must be used within ToastProvider");
    return context;
};
